import React, { useEffect, useMemo } from 'react';
import { Box, FormLabel, Stack } from '@mui/material';
import { useObjectContext } from '@/hooks/useObjectContext';
import { ComplianceTaskCheckbox } from '../cards/DealCardValues/ComplianceTaskCheckbox';
import ComplianceAccordion from '../cards/ComplianceAccordion';
import { useGetDealComplianceTasksQuery } from '@/features/engagements/engagementsApi';
import { usePipelineContext } from '@/context/pipelineContext';
import { getStepStageIds } from '@/utils/constants';

export const ComplianceTasksDisplay = () => {
	const { dealId, dealRow } = useObjectContext();

	const { data: taskData, ...tasksQuery } = useGetDealComplianceTasksQuery(
		{ dealId, includeInactive: false },
		{ skip: !dealId },
	);

	const complianceTasks = taskData ?? [];

	const pipelineId = dealRow?.pipeline;
	const { stages } = usePipelineContext(pipelineId);

	const dealStage = useMemo(() => {
		const stageId = dealRow?.dealStage;
		const stage = stages?.find((stage) => stage.id === stageId);
		return stage;
	}, [dealRow?.dealStage, stages]);

	const dealStageId = useMemo(() => dealStage?.id, [dealStage]);

	const isDisabled = Boolean(dealStage?.completed);

	const taskList = useMemo(() => {
		const stageList = stages.reduce((acc, stage) => {
			const tasks = (complianceTasks ?? []).filter((t) =>
				getStepStageIds(t).includes(stage.id),
			);

			acc.push({
				stageId: stage.id,
				order: stage.order,
				label: stage.label,
				tasks: (tasks ?? []).sort((a, b) => a.questionData.order - b.questionData.order),
			});
			return acc;
		}, []);

		if (!isDisabled) {
			return stageList.filter((s) => s.stageId == dealStageId);
		}
		return stageList.sort((a, b) => a.order - b.order).filter((t) => t.tasks.length > 0);
	}, [stages, complianceTasks, dealStageId, isDisabled]);

	return (
		<Box width={'100%'}>
			<FormLabel>Steps to complete</FormLabel>
			<Stack direction='column' spacing={1} sx={{ width: '100%' }}>
				{taskList.length > 1
					? taskList.map(({ stageId, label, tasks }) => (
						<ComplianceAccordion
							title={label}
							tasks={tasks ?? []}
							truncateTotal={true}
							key={`deal-compliance-accordion-${stageId}`}
						>
							<Stack>
								{tasks.map((task) => (
									<ComplianceTaskCheckbox key={task.documentId} task={task} />
								))}
							</Stack>
						</ComplianceAccordion>
					))
					: (taskList?.[0]?.tasks ?? []).map((task) => (
						<ComplianceTaskCheckbox key={task.documentId} task={task} />
					))}
			</Stack>
		</Box>
	);
};
